import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Spinner,
  Button,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync, faCheck } from "@fortawesome/free-solid-svg-icons";
import { fetchApi, postApi } from "../../redux/api";
import { formatNumber } from "../../redux/utils";
import CustomTable from "../../components/CustomTable";
import CustomButton from "../../components/CustomButton";
import ResponsiveContainer from "../../components/ResponsiveContainer";

function PendingRetention() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [approving, setApproving] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    fetchApi("pending-retentions")
      .then((resp) => {
        if (resp.success && Array.isArray(resp.results)) {
          setData(resp.results);
        } else {
          setData([]);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const allSelected = data.length > 0 && selected.size === data.length;

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(data.map((d) => d.retention_id)));
    }
  };

  const handleApprove = () => {
    if (selected.size === 0) return;
    setApproving(true);
    postApi("retention-payment/approve", {
      retention_ids: Array.from(selected),
    })
      .then((resp) => {
        if (resp.success) {
          setSelected(new Set());
          fetchData();
        }
      })
      .finally(() => setApproving(false));
  };

  const fields = [
    {
      title: "",
      headerComponent: () => (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={toggleSelectAll}
        />
      ),
      component: (item) => (
        <input
          type="checkbox"
          checked={selected.has(item.retention_id)}
          onChange={() => toggleSelect(item.retention_id)}
        />
      ),
      style: { textAlign: "center", width: 40 },
    },
    {
      title: "Retention ID",
      value: "retention_id",
      className: "text-center",
    },
    {
      title: "Entry Date",
      value: "entry_date",
      className: "text-center",
    },
    {
      title: "Maturity Date",
      value: "maturity_date",
      className: "text-center",
    },
    {
      title: "Contract No",
      value: "contract_no",
      className: "text-center",
    },
    {
      title: "Contractor",
      custom: true,
      component: (item) => item.contractor_name || `ID: ${item.contractor_id}`,
    },
    {
      title: "Amount Released",
      custom: true,
      component: (item) => (
        <span style={{ textAlign: "right", display: "block" }}>₦{formatNumber(item.dr || 0)}</span>
      ),
    },
    {
      title: "Status",
      custom: true,
      className: "text-center",
      component: (item) => (
        <span className="badge bg-warning text-dark">{item.status}</span>
      ),
    },
  ];

  return (
    <ResponsiveContainer>
      <Card>
        <Card.Body>
          <Row className="align-items-center mb-3">
            <Col>
              <h5 className="mb-0">Pending Retention Payments</h5>
            </Col>
            <Col xs="auto" className="d-flex gap-2">
              <Button
                variant="success"
                size="sm"
                disabled={selected.size === 0 || approving}
                onClick={handleApprove}
              >
                <FontAwesomeIcon icon={faCheck} className="me-1" />
                {approving ? "Approving..." : `Approve (${selected.size})`}
              </Button>
              <CustomButton variant="outline-primary" size="sm" onClick={fetchData}>
                <FontAwesomeIcon icon={faSync} className="me-1" />
                Refresh
              </CustomButton>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <CustomTable fields={fields} data={data} />
          )}
        </Card.Body>
      </Card>
    </ResponsiveContainer>
  );
}

export default PendingRetention;
